import {
  Component,
  Input,
  OnChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  PlasticsDeclarationReviewData,
  ProductToImport,
} from '../../../../../types/products'
import { ImportComponent } from '../../../../../types/components'
import { ImportComponentGroup } from '../../../../../types/componentGroups'
import { MatIcon } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { TranslationService } from '../../../../../services/translation.service'
import { ImportIngredient } from '../../../../../types/ingredients'
import { ImportResin } from '../../../../../types/resins'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { IngredientsSheetComponent } from './ingredients-sheet/ingredients-sheet.component'
import { ResinsSheetComponent } from './resins-sheet/resins-sheet.component'
import { ComponentsSheetComponent } from './components-sheet/components-sheet.component'
import {
  CountryRecyclability,
  ProvincialRecyclability,
} from '../../../../../types/recyclability'
import { ProvinceSheetComponent } from './province-sheet/province-sheet.component'
import { CountrySheetComponent } from './country-sheet/country-sheet.component'
import { GhsSheetComponent } from './ghs-sheet/ghs-sheet.component'
import { GhsClassification } from '../../../../../types/ghs'
import { ProductCustomFieldsComponent } from '../../../../product-custom-fields/product-custom-fields.component'
import { PlasticsDeclarationSheetComponent } from './plastics-declaration-sheet/plastics-declaration-sheet.component'

@Component({
  selector: 'app-review-products',
  imports: [ProductCustomFieldsComponent, MatIcon, MatButtonModule],
  templateUrl: './review-products.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './review-products.component.scss',
})
export class ReviewProductsComponent implements OnChanges {
  translationService = inject(TranslationService)
  private _bottomSheet = inject(MatBottomSheet)

  @Input({ required: true }) importedProducts?: Array<ProductToImport> = []
  @Input({ required: true }) errorMap: Map<string, Array<string>> = new Map()
  onlyAttention = false
  rows: Array<{
    importedProduct: ProductToImport
    errors: string[]
    ready: boolean
    details: Array<{ label: string; value: string }>
  }> = []
  otherErrors: Array<{ identifier: string; errors: string[] }> = []

  get validProducts(): number {
    return this.rows.filter((row) => row.ready).length
  }
  get attentionProducts(): number {
    return this.rows.length - this.validProducts
  }
  get visibleRows() {
    return this.onlyAttention
      ? this.rows.filter((row) => !row.ready)
      : this.rows
  }

  ngOnChanges(): void {
    const matchedKeys = new Set<string>()
    this.rows = (this.importedProducts || []).map((importedProduct) => {
      // A SKU can match another product's identifier.
      const identifier = importedProduct.product.primaryArticleIdentifier
      const errors = this.errorMap.get(identifier) || []
      if (this.errorMap.has(identifier)) matchedKeys.add(identifier)
      return {
        importedProduct,
        errors: [...new Set(errors)].map((message) =>
          this.translationService.getImportValidationText(message),
        ),
        ready: importedProduct.validationStatus === 'valid',
        details: this.productDetails(importedProduct.product),
      }
    })
    this.otherErrors = Array.from(this.errorMap.entries())
      .filter(([key, errors]) => !matchedKeys.has(key) && errors.length > 0)
      .map(([identifier, errors]) => ({
        identifier,
        errors: errors.map((message) =>
          this.translationService.getImportValidationText(message),
        ),
      }))
    if (!this.attentionProducts) this.onlyAttention = false
  }

  hasValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.some((item) => this.hasValue(item))
    if (typeof value === 'object')
      return Object.values(value).some((item) => this.hasValue(item))
    return true
  }

  private productDetails(product: ProductToImport['product']) {
    const t = this.translationService
    const fields: Array<[string, unknown]> = [
      [t.getOtherNamesText(), product.otherNames],
      [t.getTagsText(), product.plasticsDeclaration?.tags?.join(', ')],
      [t.getBrandText(), product.brand],
      [t.getSKUText(), product.sku],
      [t.getBatchText(), product.batch],
      [t.getUpperBatchRangeText(), product.batch_upper],
      [t.getSerialText(), product.serial],
      [t.getUpperSerialRangeText(), product.serial_upper],
      [t.getDescriptionText(), product.description],
      [t.getArticleCategoryText(), product.articleCategory],
      [t.getCategoryCodeText(), product.categoryCode],
      [t.getCodeDescriptionText(), product.codeDescription],
      [t.getGs1IdentifierText(), product.gs1Identifier],
      [t.getBarcodeIdentifierText(), product.barcodeIdentifier],
      [t.getEnglishShortNameText(), product.englishShortName],
      [t.getFrenchShortNameText(), product.frenchShortName],
      [t.getTypeText(), product.type],
      [t.getDiameterText(), product.diameter],
      [t.getHeightText(), product.height],
      [t.getWidthText(), product.width],
      [t.getSafeUseInstructionsText(), product.safeUseInstructions],
      [t.getDisassemblingInstructionsText(), product.disassemblingInstructions],
      [t.getProducedInCanadaText(), product.productionInCanada],
      [t.getFullIngredientDisclosureText(), product.fullIngredientDisclosure],
      [t.getIsPackageText(), product.isPackage],
      [t.getIsPlasticPackagingText(), product.isPlasticPackaging],
      [t.getIsRecyclable(), product.isRecyclable],
      [t.getPackagingTypeText(), product.packageType],
      [t.getRecycledContentPercentage(), product.recycledContentPercentage],
      [t.getMarketplaceDroneTypeText(), product.droneType],
      [t.getMarketplaceVideoResolutionText(), product.droneVideoResolution],
      [t.getMarketplaceSensorSizeText(), product.droneSensorSize],
      [t.getMarketplaceFpvText(), product.droneFpv],
    ]
    if (product.marketplaceSubcategory)
      fields.push([
        t.getMarketplaceSubcategoryLabelText(),
        t.getMarketplaceSubcategoryText(product.marketplaceSubcategory),
      ])
    if (product.condition)
      fields.push([
        t.getMarketplaceConditionText(),
        t.getMarketplaceConditionValueText(product.condition),
      ])
    if (this.hasValue(product.droneFlightTimeMinutes))
      fields.push([
        t.getMarketplaceFlightTimeText(),
        `${product.droneFlightTimeMinutes} ${t.getMarketplaceMinutesText()}`,
      ])
    if (
      this.hasValue(product.measureValue) ||
      this.hasValue(product.measureUnit)
    )
      fields.push([
        t.getMeasurementText(),
        `${product.measureValue ?? ''} ${product.measureUnit ?? ''}`.trim(),
      ])
    return fields
      .filter(([, value]) => this.hasValue(value))
      .map(([label, value]) => ({
        label,
        value:
          typeof value === 'boolean' ? this.getBoolText(value) : String(value),
      }))
  }

  openPdfInNewTab(base64String: string) {
    const pdfWindow = window.open()
    if (pdfWindow) {
      const frame = pdfWindow.document.createElement('iframe')
      frame.src = base64String
      frame.title = this.translationService.getImportAttachmentText()
      frame.style.cssText = 'width:100%;height:100vh;border:0'
      pdfWindow.document.body.appendChild(frame)
    }
  }

  /**
   * Opens bottom sheet to show a product's ingredients
   */
  showIngredients(ingredients: Array<ImportIngredient>) {
    this._bottomSheet.open(IngredientsSheetComponent, {
      data: {
        ingredients,
      },
    })
  }

  getBoolText(flag: boolean): string {
    if (flag === true) {
      return this.translationService.getYesText()
    } else if (flag === false) {
      return this.translationService.getNoText()
    } else {
      return this.translationService.getNaText()
    }
  }

  /**
   * Opens bottom sheet to show a product's provincial recyclability
   */
  showProvincialRecyclability(
    provincialRecyclability: Array<ProvincialRecyclability>,
  ) {
    this._bottomSheet.open(ProvinceSheetComponent, {
      data: {
        provincialRecyclability,
      },
    })
  }

  /**
   * Opens bottom sheet to show a product's country recyclability
   */
  showCountryRecyclability(countryRecyclability: Array<CountryRecyclability>) {
    this._bottomSheet.open(CountrySheetComponent, {
      data: {
        countryRecyclability,
      },
    })
  }

  /**
   * Opens bottom sheet to show a product's ghs classifications
   */
  showGhsClassifications(ghsClassifications: Array<GhsClassification>) {
    this._bottomSheet.open(GhsSheetComponent, {
      data: {
        ghsClassifications,
      },
    })
  }

  /**
   * Opens bottom sheet to show a product's plastics declaration
   */
  showPlasticsDeclaration(plasticsDeclaration: PlasticsDeclarationReviewData) {
    this._bottomSheet.open(PlasticsDeclarationSheetComponent, {
      data: {
        plasticsDeclaration,
      },
    })
  }

  /**
   * Opens bottom sheet to show a product's resins
   */
  showResins(resins: Array<ImportResin>) {
    this._bottomSheet.open(ResinsSheetComponent, {
      data: {
        resins,
      },
    })
  }

  /**
   * Opens bottom sheet to show a product's components.
   */
  showComponents(
    components: Array<ImportComponent> | null | undefined,
    componentGroups: Array<ImportComponentGroup> | null | undefined,
  ) {
    this._bottomSheet.open(ComponentsSheetComponent, {
      data: {
        components: components || [],
        componentGroups: componentGroups || [],
      },
    })
  }
}
